import { loadJWT } from '@/helpers/loadJWT';
import { url } from '../../api/api';
import Swal from 'sweetalert2';
import { facturaTypes } from '@/redux/types/facturaTypes';
import { Factura } from '@/types/invoices/factura';
import { setActiveInvoice } from './sales';
import { downloadBlob } from '@/utils/downloadBlob';

export const getFacturas = (id: string, offset = 0, limit = 5) => {
  return async (dispatch, getState) => {
    const { token } = loadJWT();

    dispatch({ type: facturaTypes.startLoadingFacturas });

    try {
      const filter = {
        offset,
        limit,
        order: 'FechaFactura DESC',
        fields: {
          PkFacturas: true,
          NombreFactura: true,
          TipoFactura: true,
          EstadoERP: true,
          Comentarios: true,
          ImporteFactura: true,
          FechaFactura: true,
          FechaEnvioFactura: true,
          FechaAlta: true,
        },
      };

      const resp = await url.get(`ventas/${id}/facturas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { filter: JSON.stringify(filter) },
      });

      dispatch({
        type: facturaTypes.facturasLoad,
        payload: resp.data.map((line: Factura) => ({
          ...line,
          TipoFactura: line.TipoFactura === 'RECTIFICATIVA_PARCIAL' ? 'RECTIFICATIVA' : line.TipoFactura,
        })) as Factura[],
      });
    } catch (e: any) {
      Swal.fire('Error', e?.response?.data?.error?.message || 'Error al cargar facturas', 'error');
    } finally {
      dispatch({ type: facturaTypes.finishLoadingFacturas });
    }
  };
};

export const downloadFactura = (PkFacturas: number, fileName?: string) => {
  return async () => {
    const { token } = loadJWT();

    try {
      const resp = await url.get(`facturas/${PkFacturas}/pdf`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      downloadBlob(resp.data, fileName || `factura-${PkFacturas}.pdf`, resp.headers['content-type']);
    } catch (e: any) {
      Swal.fire('Error', e?.response?.data?.error?.message || 'Error al descargar la factura', 'error');
    }
  };
};

export const downloadAllFacturas = (PkVentas: string) => {
  return async () => {
    const { token } = loadJWT();

    try {
      const resp = await url.get(`/ventas/${PkVentas}/facturas/descargar`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const contentType = resp.headers['content-type'];
      const disposition = resp.headers['content-disposition'];

      let fileName = 'facturas';

      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) {
          fileName = match[1];
        }
      } else if (contentType?.includes('zip')) {
        fileName += '.zip';
      } else {
        fileName += '.pdf';
      }

      downloadBlob(resp.data, fileName, contentType);
    } catch (e: any) {
      Swal.fire('Error', e?.response?.data?.error?.message || 'Error al descargar las facturas', 'error');
    }
  };
};

export const sendFacturaCustomer = (PkFacturas: number) => {
  return async (dispatch, getState) => {
    const { token } = loadJWT();

    await url
      .post(
        `/enviarFacturaEmailCliente/${PkFacturas}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(() => {
        Swal.fire('Éxito', 'Factura enviada', 'success');
        return;
      })
      .catch(e => {
        Swal.fire(
          'Error',
          e?.response?.data?.error?.message || 'No se ha podido enviar la factura al cliente',
          'error',
        );
        return;
      });
  };
};

export const startCreateRectificativaParcial = (PkVentas, payload, setSubmitting, toggleDialogClose) => {
  return async (dispatch, getState) => {
    const { token } = loadJWT();

    await url
      .post(`/ventas/${PkVentas}/rectificativa`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        dispatch(setActiveInvoice(PkVentas)); // Refresh active invoices
        Swal.fire('Éxito', 'Rectificativa generada correctamente', 'success');
        return;
      })
      .catch(e => {
        Swal.fire('Error', e?.response?.data?.error?.message || 'No se ha podido generar la rectificativa', 'error');
        return;
      })
      .finally(() => {
        setSubmitting(false);
        toggleDialogClose();
      });
  };
};

export const startCreateRectificativaTotal = (PkVentas, payload, onDone) => {
  return async (dispatch, getState) => {
    const { token } = loadJWT();

    await url
      .post(`/ventas/${PkVentas}/facturas/rectificarACero`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        dispatch(setActiveInvoice(PkVentas)); // Refresh active invoices
        Swal.fire('Éxito', 'Rectificativa total generada correctamente', 'success');
        return;
      })
      .catch(e => {
        Swal.fire(
          'Error',
          e?.response?.data?.error?.message || 'No se ha podido generar la rectificativa total',
          'error',
        );
        return;
      })
      .finally(() => {
        onDone();
      });
  };
};

export const startSyncFacturas = (PkVentas, onDone) => {
  return async (dispatch, getState) => {
    const { token } = loadJWT();

    try {
      const resp = await url.post(
        `/ventas/${PkVentas}/facturas/sync`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        },
      );

      dispatch(setActiveInvoice(PkVentas));
      dispatch(getFacturas(PkVentas));
      Swal.fire('Éxito', 'Sincronización con Nemon realizada correctamente', 'success');
    } catch (e: any) {
      Swal.fire('Error', e?.response?.data?.error?.message || 'No se ha podido sincronizar las facturas', 'error');
    } finally {
      onDone();
    }
  };
};